import { useAtomValue } from "jotai/react";

import {
  AGING_INTO_MEDICARE_MONTHS_TO_AWARENESS_DELTA,
  MONTH_DAYS_DELTA,
  MONTHS_TO_TURNING_65_SUBTRACTION_DELTA,
  OnboardingStepEnum,
} from "@/domain/onboarding";
import { onboardingShortCodeDataAtom } from "@/stores/atoms";
import { getDaysUntilDate, getMonthsUntilDate } from "@/utils/date";
import {
  buildOnboardingI18nKeyContext,
  checkIfAwarenessRelevantForStep,
} from "@/utils/onboarding";

export const useOnboardingEntryPointData = (
  onboardingStep: OnboardingStepEnum,
) => {
  const shortCodeData = useAtomValue(onboardingShortCodeDataAtom);

  let isAwarenessCampaign = false;
  let isMedicareActive = false;
  let isMedicaidActive = false;
  let payerName = "";

  const isAwarenessRelevantForStep = checkIfAwarenessRelevantForStep(
    shortCodeData?.entryPointType,
    onboardingStep,
  );

  if (shortCodeData?.entryPointType === "AGING_INTO_MEDICARE") {
    isAwarenessCampaign =
      getMonthsUntilDate(shortCodeData.turning65Date) >
      AGING_INTO_MEDICARE_MONTHS_TO_AWARENESS_DELTA;
  }

  if (
    shortCodeData?.entryPointType === "SERVICE_INTRODUCTION" ||
    shortCodeData?.entryPointType === "RECURRING_WORK_REQUIREMENT"
  ) {
    isMedicareActive = Boolean(shortCodeData.activeMedicare);
    isMedicaidActive = Boolean(shortCodeData.activeMedicaid);

    if (isMedicaidActive) {
      payerName = shortCodeData.activeMedicaid?.name ?? "";
    } else if (isMedicareActive) {
      payerName = shortCodeData.activeMedicare?.name ?? "";
    }
  }

  if (shortCodeData?.entryPointType === "REDETERMINATION") {
    // GitHub Issue - 425 - Redetermination awareness campaign is currently disabled
    // isAwarenessCampaign =
    //   getDaysUntilDate(shortCodeData.redeterminationDate) > MONTH_DAYS_DELTA;
    isAwarenessCampaign = false;
  }

  if (shortCodeData?.entryPointType === "RECURRING_WORK_REQUIREMENT") {
    // States without work requirement rules fall back to the usual 30 days.
    isAwarenessCampaign =
      getDaysUntilDate(shortCodeData.workRequirementDate) >
      (shortCodeData.actionCampaignThresholdDays ?? MONTH_DAYS_DELTA);
  }

  if (shortCodeData?.entryPointType === "LAPSE") {
    payerName = shortCodeData.payer?.name ?? shortCodeData.payerId ?? "";
  }

  const monthsToTurning65 =
    shortCodeData?.entryPointType === "AGING_INTO_MEDICARE"
      ? getMonthsUntilDate(shortCodeData.turning65Date)
      : undefined;

  return {
    isAwarenessCampaign,
    i18nKeyContext: buildOnboardingI18nKeyContext({
      entryPointType: shortCodeData?.entryPointType,
      isAwarenessCampaign,
      isAwarenessRelevantForStep,
      isMedicaidActive,
      isMedicareActive,
    }),
    monthsToTurning65,
    monthsToTurning65MinusDelta:
      monthsToTurning65 !== undefined
        ? monthsToTurning65 - MONTHS_TO_TURNING_65_SUBTRACTION_DELTA
        : undefined,
    isMedicareActive,
    isMedicaidActive,
    payerName,
  };
};
